import React, { useState } from "react";

import { CheckCircle2, Gift, Lock, Mail } from "lucide-react";

import { postAPIPayload } from "../../apis/postApi";
import { Button } from "../common/customDesignedComponents/button";
import { sendInviteUrl } from "./carInviteStatus";

// Same card and "Secure connection" bar as the plans screen that follows
// (SubscriptionPlansTailwind).
const SecureConnectionCard = ({ children }) => (
  <div className="tailwind_component max-w-5xl mx-auto">
    <div className="bg-card rounded-2xl border border-border shadow-lg overflow-hidden">
      <div className="bg-gradient-to-r from-secondary to-cyan-400 py-3 px-4 flex items-center justify-center gap-2">
        <Lock className="h-4 w-4 text-white" />
        <span className="text-white font-medium text-sm">
          Secure connection
        </span>
      </div>
      <div className="p-8 md:p-10">{children}</div>
    </div>
  </div>
);

// Past Client → Standard Signup, after Owner Verified: the agent either emails
// the client a free invitation or continues to buy protection (card 3841).
// Also asked in a client's Add Property flow, where "Invitation sent" returns
// to that client (doneLabel).
const CarInviteChoice = ({
  portfolioIds = [],
  onPurchase,
  onBack,
  onDone,
  doneLabel = "Back to clients",
}) => {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [sentMessage, setSentMessage] = useState("");

  const sendInvitation = async () => {
    setError("");
    if (!portfolioIds.length) {
      setError("This client is not linked to a property on your list yet.");
      return;
    }
    setSending(true);
    try {
      let lastMessage = "";
      for (const portfolioId of portfolioIds) {
        const response = await postAPIPayload({
          url: sendInviteUrl(portfolioId),
          data: {},
          token: true,
        });
        if (!response?.success) {
          throw new Error(
            response?.message || "Unable to send the invitation.",
          );
        }
        lastMessage = response?.message || "";
      }
      setSentMessage(lastMessage || "Invitation sent.");
    } catch (sendError) {
      setError(sendError?.message || "Unable to send the invitation.");
    } finally {
      setSending(false);
    }
  };

  if (sentMessage) {
    return (
      <SecureConnectionCard>
        <div className="mx-auto max-w-2xl text-center">
          <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-emerald-600" />
          <h2 className="text-2xl font-semibold text-foreground">
            Invitation sent
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">{sentMessage}</p>
          <p className="mt-2 text-sm text-muted-foreground">
            We will remind your client every 5 days for 30 days, until they view
            their risk assessment or decline. You can follow the status on your
            Clients page.
          </p>
          <Button type="button" className="mt-6" onClick={onDone}>
            {doneLabel}
          </Button>
        </div>
      </SecureConnectionCard>
    );
  }

  return (
    <SecureConnectionCard>
      <div className="mx-auto max-w-4xl space-y-6">
        <div className="space-y-2 text-center">
          <h2 className="text-2xl font-semibold text-foreground">
            How Would You Like to Introduce EquityProtect to Your Client?
          </h2>
          <p className="text-sm text-muted-foreground">
            As their trusted real estate agent, your relationship with your
            clients doesn&apos;t end at closing. EquityProtect provides an
            opportunity to continue looking out for your past clients by
            educating them about property fraud and offering proactive
            protection for their homes.
          </p>
          <p className="text-sm font-medium text-foreground">
            Choose how you would like to proceed.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col rounded-2xl border border-border bg-card p-6">
            <Mail className="mb-3 h-7 w-7 text-primary" />
            <p className="text-lg font-semibold text-foreground">
              Send an Educational Invitation
            </p>
            <p className="mt-2 flex-1 text-sm text-muted-foreground">
              Send your client a personalized email from you explaining the
              growing risk of property fraud and introducing EquityProtect as a
              way to help protect their home. The email invites the homeowner to
              view their property&apos;s fraud risk assessment and learn about
              available protection options. There is no purchase required by you
              to send this invitation.
            </p>
            <Button
              type="button"
              className="mt-5"
              onClick={sendInvitation}
              disabled={sending}
            >
              {sending ? "Sending..." : "Send an Invitation to My Client"}
            </Button>
          </div>

          <div className="flex flex-col rounded-2xl border border-border bg-card p-6">
            <Gift className="mb-3 h-7 w-7 text-primary" />
            <p className="text-lg font-semibold text-foreground">
              Purchase Protection
            </p>
            <p className="mt-2 flex-1 text-sm text-muted-foreground">
              Purchase EquityProtect protection on behalf of your client as a
              gift, providing an additional level of service and protection
              beyond the original real estate transaction. You will choose the
              protection term and complete payment next.
            </p>
            <Button
              type="button"
              variant="outline"
              className="mt-5"
              onClick={onPurchase}
              disabled={sending}
            >
              I Will Purchase This Protection for My Client
            </Button>
          </div>
        </div>

        {error ? (
          <p className="text-center text-sm text-destructive">{error}</p>
        ) : null}

        {onBack ? (
          <div className="text-center">
            <Button
              type="button"
              variant="ghost"
              onClick={onBack}
              disabled={sending}
            >
              Back
            </Button>
          </div>
        ) : null}
      </div>
    </SecureConnectionCard>
  );
};

export default CarInviteChoice;
